import { ReactElement, useState } from 'react'
import { SelectChangeEvent, Dialog, DialogContent, DialogTitle, DialogActions } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCompanySpaces } from '../queries/useCompanyQueries'
import { adjustColor } from '../theme/ajustColor'
import { useUI } from '../hooks/useUI'

type ListItem =
  | string
  | {
      label: string
      disabled?: boolean
      available?: boolean
    }

interface CustomMultiSelectProps {
  label?: string
  value?: string[]
  onChange?: (event: SelectChangeEvent<string[]>) => void
  listItems: ListItem[]
  required?: boolean
  error?: boolean
  helperText?: string
  validation?: { validate: () => boolean; msg: string }[]
  className?: string
  disabled?: boolean
  multiselect?: boolean
}

const CustomMultiSelect = ({
  label,
  value = [],
  listItems,
  required,
  validation,
  error,
  helperText,
  className,
  onChange,
  disabled,
  multiselect = true,
  ...rest
}: CustomMultiSelectProps): ReactElement => {
  const { t } = useTranslation()
  const [touched, setTouched] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(value)
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  const handleToggle = (itemLabel: string) => {
    if (disabled) return
    setTouched(true)

    if (multiselect) {
      setTempSelectedValues((prev) =>
        prev.includes(itemLabel) ? prev.filter((v) => v !== itemLabel) : [...prev, itemLabel]
      )
    } else {
      setTempSelectedValues([itemLabel])
    }
  }

  const handleConfirm = () => {
    if (!onChange) return

    const selectChangeEvent: SelectChangeEvent<string[]> = {
      target: {
        value: tempSelectedValues,
        name: 'multiselect'
      }
    } as SelectChangeEvent<string[]>

    onChange(selectChangeEvent)
    setIsOpen(false)

    // Si no es multiselect, cerrar automáticamente al seleccionar
    if (!multiselect && tempSelectedValues.length === 1) {
      setIsOpen(false)
    }
  }

  const handleOpen = () => {
    if (disabled) return
    setTempSelectedValues(value)
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setTempSelectedValues(value)
  }

  const getMessageError = () => {
    if (validation) {
      for (const v of validation) {
        if (!v.validate()) return v.msg
      }
      return ''
    }
  }

  const requiredCondition = touched && (!value || value.length === 0) && required
  const showCustomError = touched && validation && !!getMessageError()
  const hasError = error || requiredCondition || showCustomError
  const errorMessage = helperText || (requiredCondition ? t('requiredField') : getMessageError())

  return (
    <div>
      {label && (
        <label style={{ color: colors?.text }} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div
        onClick={handleOpen}
        className={`
          w-full px-3 py-2 border rounded-md cursor-pointer select-none
          ${hasError ? 'border-red-500' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
          ${className || ''}
        `}
        style={{
          backgroundColor: colors?.background,
          borderColor: darkMode ? colors?.dark : colors?.light,
          color: colors?.text
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 flex-1 min-h-[24px]">
            {value && value.length > 0 ? (
              value.map((selected) => (
                <span
                  key={selected}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                  style={{
                    backgroundColor: colors?.light,
                    color: colors?.text
                  }}
                >
                  {selected}
                </span>
              ))
            ) : (
              <span className="text-gray-500">{t('select')}</span>
            )}
          </div>
          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: colors?.background,
            color: colors?.text
          }
        }}
      >
        <DialogTitle style={{ color: colors?.text }}>
          {label || t('select')}
          {!multiselect && (
            <span className="text-xs ml-2 opacity-70">({t('singleSelection')})</span>
          )}
        </DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-4">
            {listItems.map((item) => {
              const itemObj = typeof item === 'string' ? { label: item } : item
              const isChecked = tempSelectedValues.includes(itemObj.label)
              const isDisabled = disabled || itemObj.disabled

              return (
                <div
                  key={itemObj.label}
                  className={`
                    flex items-center p-3 rounded-lg border
                    ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-black hover:bg-opacity-5'
                    }
                    ${isChecked ? 'border-blue-500' : 'border-gray-300'}
                  `}
                  onClick={() => !isDisabled && handleToggle(itemObj.label)}
                  style={{
                    borderColor: isChecked ? colors?.main : darkMode ? colors?.dark : colors?.light
                  }}
                >
                  <input
                    type={multiselect ? 'checkbox' : 'radio'}
                    checked={isChecked}
                    onChange={() => {}}
                    disabled={isDisabled}
                    className={`w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500 ${
                      multiselect ? 'rounded' : 'rounded-full'
                    }`}
                  />
                  <span className="ml-2 text-sm" style={{ color: colors?.text }}>
                    {itemObj.label}
                  </span>
                </div>
              )
            })}
          </div>
        </DialogContent>
        <DialogActions className="p-4 flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{
              backgroundColor: 'transparent',
              color: colors?.text,
              border: `1px solid ${colors?.text}`
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 rounded-md text-sm font-medium text-white"
            style={{
              backgroundColor: colors?.main
            }}
          >
            {t('confirm')}
          </button>
        </DialogActions>
      </Dialog>

      {hasError && errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
    </div>
  )
}

export default CustomMultiSelect
